/** SUNTRONIX ONE — Shadow Billing & Monthly Billing Certification.
 *
 *  Prove-before-you-invoice workspace: line-by-line comparison of
 *  SUNTRONIX-generated billing against Finance-verified billing, with
 *  variance workspace, owner routing, and a certification ladder that
 *  gates the Live Invoice switch.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/app/PageHeader";
import Section from "@/components/app/Section";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  ShieldCheck, ShieldAlert, Upload, GitCompare, Send,
  RefreshCcw, Plus, AlertTriangle, CheckCircle2, XCircle, Lock,
  Users, FileSpreadsheet, Layers,
} from "lucide-react";

const MODE_TONE = {
  CONFIGURATION:                    "bg-slate-100 text-slate-700",
  SHADOW_BILLING:                   "bg-amber-100 text-amber-900",
  CERTIFIED_MANUAL_INVOICE:         "bg-sky-100 text-sky-800",
  CERTIFIED_SYSTEM_INVOICE_DRAFT:   "bg-indigo-100 text-indigo-800",
  LIVE_SYSTEM_INVOICE:              "bg-emerald-100 text-emerald-800",
  SUSPENDED:                        "bg-rose-100 text-rose-800",
};

const PERIOD_TONE = {
  DRAFT:                       "bg-slate-100 text-slate-700",
  COLLECTING:                  "bg-slate-100 text-slate-700",
  SYSTEM_STATEMENT_READY:      "bg-sky-100 text-sky-800",
  MANUAL_STATEMENT_UPLOADED:   "bg-sky-100 text-sky-800",
  RECONCILING:                 "bg-amber-100 text-amber-900",
  VARIANCES_IDENTIFIED:        "bg-amber-100 text-amber-900",
  CORRECTION_REQUIRED:         "bg-rose-100 text-rose-800",
  READY_FOR_CERTIFICATION:     "bg-teal-100 text-teal-800",
  CERTIFIED:                   "bg-emerald-100 text-emerald-800",
  CERTIFIED_WITH_CONDITIONS:   "bg-emerald-50 text-emerald-800",
  FAILED:                      "bg-rose-200 text-rose-900",
  CANCELLED:                   "bg-slate-200 text-slate-500",
};

const RECON_TONE = {
  EXACT_MATCH:              "bg-emerald-100 text-emerald-800",
  QUANTITY_VARIANCE:        "bg-amber-100 text-amber-900",
  RATE_VARIANCE:            "bg-amber-100 text-amber-900",
  AMOUNT_VARIANCE:          "bg-amber-100 text-amber-900",
  TAX_VARIANCE:             "bg-amber-100 text-amber-900",
  PO_MISMATCH:              "bg-amber-100 text-amber-900",
  CURRENCY_MISMATCH:        "bg-rose-100 text-rose-800",
  ORDER_ID_MISMATCH:        "bg-rose-100 text-rose-800",
  SERVICE_MISMATCH:         "bg-rose-100 text-rose-800",
  CUSTOMER_MISMATCH:        "bg-rose-100 text-rose-800",
  MISSING_IN_SYSTEM:        "bg-rose-100 text-rose-800",
  MISSING_IN_MANUAL:        "bg-rose-100 text-rose-800",
  POSSIBLE_DUPLICATE:       "bg-rose-200 text-rose-900",
  REQUIRES_REVIEW:          "bg-slate-100 text-slate-700",
  MATCH_WITH_NON_FINANCIAL_DIFFERENCE: "bg-teal-100 text-teal-800",
};

const VARIANCE_STATES = [
  "OPEN", "UNDER_REVIEW",
  "WAITING_ON_OPERATIONS", "WAITING_ON_FINANCE",
  "WAITING_ON_COMMERCIAL", "WAITING_ON_CUSTOMER",
  "CORRECTION_IN_PROGRESS", "CORRECTED_IN_SYSTEM",
  "CORRECTED_IN_MANUAL", "ACCEPTED_EXCEPTION",
  "RESOLVED", "CANCELLED",
];

const BILLING_MODES = [
  "CONFIGURATION", "SHADOW_BILLING",
  "CERTIFIED_MANUAL_INVOICE", "CERTIFIED_SYSTEM_INVOICE_DRAFT",
  "LIVE_SYSTEM_INVOICE", "SUSPENDED",
];

const TABS = [
  { id: "overview",       label: "Overview" },
  { id: "customers",      label: "Customers in Shadow" },
  { id: "system",         label: "System-Generated" },
  { id: "finance",        label: "Finance-Verified" },
  { id: "reconciliation", label: "Reconciliation" },
  { id: "variances",      label: "Variances" },
  { id: "certification",  label: "Certification" },
  { id: "history",        label: "History" },
];

const fmt = (n, ccy = "INR") =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: ccy || "INR", maximumFractionDigits: 2 }).format(n || 0);


export default function ShadowBilling() {
  const [tab, setTab] = useState("overview");
  const [dashboard, setDashboard] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [activePeriodId, setActivePeriodId] = useState(null);
  const [certifications, setCertifications] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [showMode, setShowMode] = useState(null);       // profile object
  const [showOverride, setShowOverride] = useState(null); // profile object

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [dash, pf, per, cert] = await Promise.all([
        api.get("/shadow-billing/dashboard"),
        api.get("/shadow-billing/profiles"),
        api.get("/shadow-billing/periods"),
        api.get("/shadow-billing/certifications"),
      ]);
      setDashboard(dash.data);
      setProfiles(pf.data.items || []);
      setPeriods(per.data.items || []);
      setCertifications(cert.data.items || []);
      if (!activePeriodId && per.data.items?.length) {
        setActivePeriodId(per.data.items[0].id);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Load failed");
    } finally { setBusy(false); }
  }, [activePeriodId]);

  useEffect(() => { load(); }, [load]);

  const activePeriod = useMemo(
    () => periods.find((p) => p.id === activePeriodId) || periods[0] || null,
    [periods, activePeriodId]
  );

  return (
    <div className="space-y-6 max-w-[1500px]" data-testid="shadow-billing">
      <PageHeader
        eyebrow="Commercial · Monthly Billing · SHADOW"
        title="Shadow Billing & Monthly Billing Certification"
        context="Prove before you invoice. SUNTRONIX ONE and Finance produce the same monthly statement in parallel, and every deviation is worked to resolution — no customer sees a live invoice until certification passes."
        actions={
          <Button variant="outline" onClick={load} disabled={busy}
                  data-testid="shadow-refresh-btn">
            <RefreshCcw className={`h-3.5 w-3.5 mr-1.5 ${busy ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        }
      />

      {/* Mode tiles */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
        {BILLING_MODES.map((m) => (
          <div key={m} className="st-panel p-3" data-testid={`shadow-mode-tile-${m}`}>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 truncate" title={m}>{m.replace(/_/g, " ")}</div>
            <div className="text-2xl font-heading font-semibold tabular-nums">
              {dashboard?.mode_counts?.[m] || 0}
            </div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-slate-200 overflow-x-auto">
        {TABS.map((t) => (
          <button key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-3 py-2 text-[12px] font-medium tracking-wide border-b-2 -mb-px whitespace-nowrap transition-colors
              ${tab === t.id
                ? "border-copper-600 text-copper-700"
                : "border-transparent text-slate-500 hover:text-slate-700"}`}
            data-testid={`shadow-tab-${t.id}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <OverviewTab dashboard={dashboard} periods={periods}
                     onSelectPeriod={setActivePeriodId}
                     onCreatePeriod={() => setShowCreate(true)} />
      )}
      {tab === "customers" && (
        <CustomersTab profiles={profiles} onMode={setShowMode}
                       onOverride={setShowOverride} onReload={load} />
      )}
      {(tab === "system" || tab === "finance" || tab === "reconciliation" ||
        tab === "variances" || tab === "certification") && (
        <PeriodWorkspace tab={tab} periods={periods}
                          activePeriod={activePeriod}
                          onSelectPeriod={setActivePeriodId}
                          onReload={load} />
      )}
      {tab === "history" && (
        <HistoryTab periods={periods} certifications={certifications} />
      )}

      {showCreate && (
        <CreatePeriodDialog onClose={() => setShowCreate(false)}
                            onCreated={() => { setShowCreate(false); load(); }} />
      )}
      {showMode && (
        <ChangeModeDialog profile={showMode}
                          onClose={() => setShowMode(null)}
                          onChanged={() => { setShowMode(null); load(); }} />
      )}
      {showOverride && (
        <OverrideDialog profile={showOverride}
                        onClose={() => setShowOverride(null)}
                        onChanged={() => { setShowOverride(null); load(); }} />
      )}
    </div>
  );
}


/* -------------------------------------------------------------------------- */
/*  Overview                                                                  */
/* -------------------------------------------------------------------------- */
function OverviewTab({ dashboard, periods, onSelectPeriod, onCreatePeriod }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="lg:col-span-2 space-y-4">
        <Section title="Recent shadow periods"
                 actions={
                   <Button size="sm" onClick={onCreatePeriod}
                           data-testid="shadow-create-btn">
                     <Plus className="h-3 w-3 mr-1" /> New shadow period
                   </Button>
                 }
                 testId="shadow-recent-periods">
          <div className="st-panel overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-3 py-2">Shadow #</th>
                  <th className="text-left px-3 py-2">Customer</th>
                  <th className="text-left px-3 py-2">Period</th>
                  <th className="text-right px-3 py-2">Manual</th>
                  <th className="text-right px-3 py-2">System</th>
                  <th className="text-right px-3 py-2">Variance</th>
                  <th className="text-left px-3 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {(dashboard?.recent_periods || periods.slice(0, 8)).map((p) => (
                  <tr key={p.id} onClick={() => onSelectPeriod(p.id)}
                      className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                      data-testid={`shadow-recent-row-${p.id}`}>
                    <td className="px-3 py-2 font-mono text-[11px]">{p.shadow_number}</td>
                    <td className="px-3 py-2">{p.customer_name}</td>
                    <td className="px-3 py-2 text-[11px] text-slate-500">{p.billing_period}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmt(p.manual_total, p.currency)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmt(p.system_total, p.currency)}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-semibold">{fmt(p.variance_total, p.currency)}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-wider ${PERIOD_TONE[p.status] || "bg-slate-100 text-slate-700"}`}>
                        {p.status}
                      </span>
                    </td>
                  </tr>
                ))}
                {periods.length === 0 && (
                  <tr><td colSpan={7} className="text-center py-8 text-[12px] text-slate-400">
                    No shadow periods yet — create your first one to start.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Section>
      </div>
      <Section title="Certification queue" testId="shadow-cert-queue">
        <div className="st-panel p-3 space-y-2">
          <div className="flex items-center justify-between text-[12px]">
            <span className="text-slate-500">Open variances</span>
            <span className="font-semibold text-amber-800">{dashboard?.open_variances || 0}</span>
          </div>
          {Object.entries(dashboard?.period_counts || {})
            .filter(([, n]) => n > 0)
            .map(([k, n]) => (
              <div key={k} className="flex items-center justify-between text-[11px]">
                <span className="text-slate-600">{k.replace(/_/g, " ")}</span>
                <span className="font-mono">{n}</span>
              </div>
            ))}
          {(dashboard?.recent_certifications || []).map((c) => (
            <div key={c.id} className="border-t border-slate-100 pt-2 mt-2">
              <div className="text-[11px] font-mono">{c.certification_number}</div>
              <div className="text-[10px] text-slate-500">
                L{c.level} · {c.customer_name} · {c.billing_period}
                {c.with_conditions && (
                  <span className="ml-1 text-amber-700">· with conditions</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}


/* -------------------------------------------------------------------------- */
/*  Customers in Shadow                                                        */
/* -------------------------------------------------------------------------- */
function CustomersTab({ profiles, onMode, onOverride }) {
  return (
    <Section title="Customer billing profiles"
             hint="Every customer sits in exactly one billing mode. The Live-Invoice gate reads from here."
             testId="shadow-customers-section">
      <div className="st-panel overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-3 py-2">Customer</th>
              <th className="text-left px-3 py-2">Mode</th>
              <th className="text-left px-3 py-2">Override</th>
              <th className="text-left px-3 py-2">Last mode change</th>
              <th className="text-right px-3 py-2 pr-4"></th>
            </tr>
          </thead>
          <tbody>
            {profiles.map((p) => {
              const lastHist = (p.mode_history || []).slice(-1)[0];
              return (
                <tr key={p.id} className="border-t border-slate-100"
                    data-testid={`shadow-profile-${p.master_company_id}`}>
                  <td className="px-3 py-2">{p.customer_name}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-wider ${MODE_TONE[p.billing_mode] || "bg-slate-100 text-slate-700"}`}>
                      {(p.billing_mode || "CONFIGURATION").replace(/_/g, " ")}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-[11px]">
                    {p.emergency_override_active
                      ? <span className="text-rose-700 font-semibold">ACTIVE until {p.emergency_override_expires_at?.slice(0, 16)}</span>
                      : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-3 py-2 text-[11px] text-slate-500">
                    {lastHist ? `${lastHist.to_mode.replace(/_/g," ")} · ${lastHist.at?.slice(0, 10)}` : "—"}
                  </td>
                  <td className="px-3 py-2 pr-4 text-right space-x-2">
                    <Button size="sm" variant="outline" onClick={() => onMode(p)}
                            data-testid={`shadow-change-mode-${p.master_company_id}`}>
                      Change mode
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => onOverride(p)}
                            className="text-rose-700 hover:bg-rose-50"
                            data-testid={`shadow-override-${p.master_company_id}`}>
                      <Lock className="h-3 w-3 mr-1" /> Override
                    </Button>
                  </td>
                </tr>
              );
            })}
            {profiles.length === 0 && (
              <tr><td colSpan={5} className="text-center py-8 text-[12px] text-slate-400">
                No customer billing profiles yet — creating a shadow period auto-initialises one.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </Section>
  );
}


/* -------------------------------------------------------------------------- */
/*  Period workspace (System / Finance / Reconciliation / Variances / Certify) */
/* -------------------------------------------------------------------------- */
function PeriodWorkspace({ tab, periods, activePeriod, onSelectPeriod, onReload }) {
  if (!activePeriod) {
    return (
      <div className="st-panel p-6 text-center text-[12px] text-slate-400">
        Select or create a shadow period to work on.
      </div>
    );
  }
  return (
    <div className="space-y-4">
      <PeriodPicker periods={periods} value={activePeriod.id} onChange={onSelectPeriod} />
      <PeriodSummary p={activePeriod} onReload={onReload} />
      {tab === "system"         && <SystemTab p={activePeriod} onReload={onReload} />}
      {tab === "finance"        && <FinanceTab p={activePeriod} onReload={onReload} />}
      {tab === "reconciliation" && <ReconciliationTab p={activePeriod} onReload={onReload} />}
      {tab === "variances"      && <VariancesTab p={activePeriod} onReload={onReload} />}
      {tab === "certification"  && <CertificationTab p={activePeriod} onReload={onReload} />}
    </div>
  );
}

function PeriodPicker({ periods, value, onChange }) {
  return (
    <div className="flex items-center gap-2 text-[12px]">
      <span className="text-slate-500">Active shadow period:</span>
      <select value={value} onChange={(e) => onChange(e.target.value)}
              className="border rounded h-8 px-2 text-[12px]"
              data-testid="shadow-period-picker">
        {periods.map((p) => (
          <option key={p.id} value={p.id}>
            {`${p.shadow_number} · ${p.customer_name} · ${p.billing_period} · ${p.status}`}
          </option>
        ))}
      </select>
    </div>
  );
}

function PeriodSummary({ p, onReload }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-6 gap-2 text-[13px]"
         data-testid="shadow-period-summary">
      <FieldTile label="Shadow #" value={p.shadow_number} mono />
      <FieldTile label="Customer" value={p.customer_name} />
      <FieldTile label="Period" value={p.billing_period} />
      <FieldTile label="Manual total" value={fmt(p.manual_total, p.currency)} />
      <FieldTile label="System total" value={fmt(p.system_total, p.currency)} />
      <FieldTile label="Variance" value={fmt(p.variance_total, p.currency)} bold
                 tone={Math.abs(p.variance_total || 0) < 0.01 ? "text-emerald-700" : "text-amber-800"} />
    </div>
  );
}

function FieldTile({ label, value, bold, mono, tone }) {
  return (
    <div className="st-panel p-2">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-[13px] tabular-nums truncate ${bold ? "font-semibold" : ""} ${mono ? "font-mono text-[11px]" : ""} ${tone || ""}`}>
        {value ?? "—"}
      </div>
    </div>
  );
}


/* ---------- System-Generated tab ---------- */
function SystemTab({ p, onReload }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (!window.confirm("Rebuild the system-side snapshot from completed billable_events?")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/shadow-billing/periods/${p.id}/generate-system`);
      toast.success(`System snapshot ready — ${data.lines} line(s), ${fmt(data.grand_total, p.currency)}`);
      onReload?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };
  const snap = p.system_snapshot || [];
  return (
    <Section title="System-generated snapshot"
             hint="Snapshot is read-only — the authoritative billable_events remain untouched."
             actions={
               <Button size="sm" onClick={run} disabled={busy}
                       data-testid="shadow-generate-system-btn">
                 <FileSpreadsheet className="h-3 w-3 mr-1" /> Regenerate
               </Button>
             }
             testId="shadow-system-section">
      <div className="st-panel overflow-x-auto">
        <table className="w-full text-[12px]">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-3 py-2">Order ID</th>
              <th className="text-left px-3 py-2">Service</th>
              <th className="text-left px-3 py-2">Date</th>
              <th className="text-right px-3 py-2">Qty</th>
              <th className="text-right px-3 py-2">Rate</th>
              <th className="text-right px-3 py-2">Amount</th>
              <th className="text-right px-3 py-2">Tax</th>
              <th className="text-left px-3 py-2">PO</th>
            </tr>
          </thead>
          <tbody>
            {snap.map((s, i) => (
              <tr key={s.event_id || i} className="border-t border-slate-100">
                <td className="px-3 py-1.5 font-mono text-[11px]">{s.order_id}</td>
                <td className="px-3 py-1.5">{s.service_name || s.service_code}</td>
                <td className="px-3 py-1.5 text-[11px] text-slate-500">{s.service_date}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{s.quantity}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{fmt(s.rate, s.currency)}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{fmt(s.amount, s.currency)}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{fmt(s.tax, s.currency)}</td>
                <td className="px-3 py-1.5 font-mono text-[11px]">{s.po || "—"}</td>
              </tr>
            ))}
            {snap.length === 0 && (
              <tr><td colSpan={8} className="text-center py-8 text-[12px] text-slate-400">
                No system snapshot yet — click Regenerate.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </Section>
  );
}


/* ---------- Finance-Verified tab ---------- */
function FinanceTab({ p, onReload }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [lines, setLines] = useState([]);
  const [mapping, setMapping] = useState(p.column_mapping || {});

  const loadLines = useCallback(async () => {
    try {
      const { data } = await api.get("/shadow-billing/periods/" + p.id + "/reconciliations");
      // reconciliations already carries manual_amount — display raw manual lines instead
      const manualRes = await api.get(`/shadow-billing/periods/${p.id}`);
      const _ = manualRes;
      setLines(data.items || []);
    } catch { /* ignore */ }
  }, [p.id]);
  useEffect(() => { loadLines(); }, [loadLines]);

  const upload = async () => {
    if (!file) { toast.error("Select a CSV/XLSX file"); return; }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      if (Object.keys(mapping).length) form.append("mapping_json", JSON.stringify(mapping));
      const { data } = await api.post(
        `/shadow-billing/periods/${p.id}/upload-manual`, form,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.lines} finance line(s) · total ${fmt(data.grand_total, p.currency)}`);
      setMapping(data.column_mapping || {});
      onReload?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  return (
    <Section title="Finance-verified statement upload"
             hint="Upload the manual XLSX or CSV Finance uses today. SUNTRONIX auto-detects columns; override in the mapping field below if needed."
             testId="shadow-finance-section">
      <div className="st-panel p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <input type="file" accept=".xlsx,.xls,.csv"
                 onChange={(e) => setFile(e.target.files?.[0] || null)}
                 data-testid="shadow-manual-file" />
          <Button onClick={upload} disabled={busy || !file}
                  data-testid="shadow-manual-upload-btn">
            <Upload className="h-3 w-3 mr-1" /> Upload
          </Button>
          <div className="text-[11px] text-slate-500 ml-auto">
            Current: {p.manual_line_count || 0} line(s) · {fmt(p.manual_total, p.currency)}
          </div>
        </div>
        {Object.keys(mapping).length > 0 && (
          <details className="text-[11px]">
            <summary className="cursor-pointer text-slate-500">Column mapping ({Object.keys(mapping).length})</summary>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-1 mt-2">
              {Object.entries(mapping).map(([k, v]) => (
                <div key={k} className="font-mono">
                  <span className="text-slate-500">{k}</span> → <span>{v}</span>
                </div>
              ))}
            </div>
          </details>
        )}
      </div>
    </Section>
  );
}


/* ---------- Reconciliation tab ---------- */
function ReconciliationTab({ p, onReload }) {
  const [busy, setBusy] = useState(false);
  const [recons, setRecons] = useState([]);
  const [filter, setFilter] = useState("");
  const [showBulk, setShowBulk] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/shadow-billing/periods/${p.id}/reconciliations`);
      setRecons(data.items || []);
    } catch { /* ignore */ }
  }, [p.id]);
  useEffect(() => { load(); }, [load]);

  const run = async () => {
    if (!window.confirm("Rerun the line-by-line reconciliation? Existing variance rows will be regenerated.")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/shadow-billing/periods/${p.id}/reconcile`, {
        rounding_tolerance: 0.5, tax_tolerance: 0.5,
      });
      toast.success(`${data.recon_count} recon rows · ${data.variance_count} variance(s)`);
      load(); onReload?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const filtered = filter ? recons.filter((r) => r.status === filter) : recons;
  const grouped = useMemo(() => {
    const g = {};
    for (const r of recons) g[r.status] = (g[r.status] || 0) + 1;
    return g;
  }, [recons]);
  const exactCount = grouped.EXACT_MATCH || 0;

  return (
    <Section title={`Line-by-line reconciliation (${recons.length})`}
             actions={
               <div className="flex items-center gap-2">
                 <Button size="sm" variant="outline" onClick={() => setShowBulk(true)}
                         disabled={exactCount === 0}
                         data-testid="shadow-bulk-certify-btn">
                   <ShieldCheck className="h-3 w-3 mr-1" /> Bulk certify ({exactCount})
                 </Button>
                 <Button size="sm" onClick={run} disabled={busy}
                         data-testid="shadow-reconcile-btn">
                   <GitCompare className="h-3 w-3 mr-1" /> Run reconciliation
                 </Button>
               </div>
             }
             testId="shadow-recon-section">
      <div className="flex flex-wrap gap-1 mb-3">
        <button onClick={() => setFilter("")}
                className={`px-2 py-1 rounded text-[10px] font-semibold ${!filter ? "bg-slate-200" : "bg-slate-50"}`}>
          All ({recons.length})
        </button>
        {Object.entries(grouped).map(([k, n]) => (
          <button key={k} onClick={() => setFilter(k)}
                  className={`px-2 py-1 rounded text-[10px] font-semibold ${filter === k ? "bg-slate-200" : ""} ${RECON_TONE[k] || "bg-slate-50"}`}
                  data-testid={`shadow-recon-filter-${k}`}>
            {k.replace(/_/g, " ")} ({n})
          </button>
        ))}
      </div>
      <div className="st-panel overflow-x-auto">
        <table className="w-full text-[12px]">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2">Order ID</th>
              <th className="text-left px-3 py-2">Service</th>
              <th className="text-right px-3 py-2">Manual</th>
              <th className="text-right px-3 py-2">System</th>
              <th className="text-right px-3 py-2">Diff</th>
              <th className="text-right px-3 py-2">Diff %</th>
              <th className="text-left px-3 py-2">Notes</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.id} className="border-t border-slate-100"
                  data-testid={`shadow-recon-row-${r.id}`}>
                <td className="px-3 py-1.5">
                  <div className="flex flex-col gap-0.5">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-wider ${RECON_TONE[r.status] || "bg-slate-100"}`}>
                      {r.status.replace(/_/g, " ")}
                    </span>
                    {r.certification_status === "CERTIFIED_BULK" && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-semibold tracking-wider bg-emerald-100 text-emerald-800"
                            title={`Batch ${r.certification_batch_number}`}
                            data-testid={`shadow-cert-badge-${r.id}`}>
                        CERTIFIED · {r.certification_batch_number}
                      </span>
                    )}
                    {r.certification_status === "REQUIRES_RECERTIFICATION" && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-semibold tracking-wider bg-amber-200 text-amber-900"
                            title={r.recertification_reason || "changed after certification"}
                            data-testid={`shadow-recert-badge-${r.id}`}>
                        REQUIRES RECERT
                      </span>
                    )}
                    {r.certification_status === "CERTIFICATION_REVERSED" && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-semibold tracking-wider bg-slate-200 text-slate-600 line-through"
                            data-testid={`shadow-reversed-badge-${r.id}`}>
                        REVERSED
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-3 py-1.5 font-mono text-[11px]">{r.order_id || "—"}</td>
                <td className="px-3 py-1.5 font-mono text-[11px]">{r.service_code || "—"}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{fmt(r.manual_amount + r.manual_tax, r.currency)}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{fmt(r.system_amount + r.system_tax, r.currency)}</td>
                <td className="px-3 py-1.5 text-right tabular-nums font-semibold">{fmt(r.variance_amount, r.currency)}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{r.variance_pct}%</td>
                <td className="px-3 py-1.5 text-[11px] text-slate-600">{r.notes || "—"}</td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={8} className="text-center py-8 text-[12px] text-slate-400">
                No reconciliation rows — click &quot;Run reconciliation&quot; after uploading manual + generating system.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
      {showBulk && (
        <BulkCertifyDialog periodId={p.id} currency={p.currency}
                            onClose={() => setShowBulk(false)}
                            onCertified={() => { setShowBulk(false); load(); onReload?.(); }} />
      )}
    </Section>
  );
}


/* ---------- Variances tab ---------- */
function VariancesTab({ p, onReload }) {
  const [variances, setVariances] = useState([]);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/shadow-billing/periods/${p.id}/variances`);
      setVariances(data.items || []);
    } catch { /* ignore */ }
  }, [p.id]);
  useEffect(() => { load(); }, [load]);

  return (
    <Section title={`Variance workspace (${variances.length})`}
             hint="Every non-EXACT reconciliation row lands here. Assign an owner and drive to resolution before certification."
             testId="shadow-variance-section">
      <div className="st-panel overflow-x-auto">
        <table className="w-full text-[12px]">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-3 py-2">Order ID</th>
              <th className="text-left px-3 py-2">Service</th>
              <th className="text-left px-3 py-2">Cause</th>
              <th className="text-left px-3 py-2">Owner</th>
              <th className="text-right px-3 py-2">Variance</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-right px-3 py-2 pr-4"></th>
            </tr>
          </thead>
          <tbody>
            {variances.map((v) => (
              <tr key={v.id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                  onClick={() => setSelected(v)}
                  data-testid={`shadow-variance-row-${v.id}`}>
                <td className="px-3 py-1.5 font-mono text-[11px]">{v.order_id || "—"}</td>
                <td className="px-3 py-1.5 font-mono text-[11px]">{v.service_code || "—"}</td>
                <td className="px-3 py-1.5 text-[11px]">{(v.cause || "").replace(/_/g, " ")}</td>
                <td className="px-3 py-1.5 text-[11px]">
                  <span className="font-semibold">{v.owner_role}</span>
                  {v.owner_email && <span className="text-slate-500"> · {v.owner_email}</span>}
                </td>
                <td className="px-3 py-1.5 text-right tabular-nums font-semibold">{fmt(v.variance_amount)}</td>
                <td className="px-3 py-1.5">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-wider bg-slate-100">
                    {v.status.replace(/_/g, " ")}
                  </span>
                </td>
                <td className="px-3 py-1.5 pr-4 text-right">
                  <Button size="sm" variant="ghost">Work</Button>
                </td>
              </tr>
            ))}
            {variances.length === 0 && (
              <tr><td colSpan={7} className="text-center py-8 text-[12px] text-slate-400">
                No open variances — customer is ready for certification (once reconciliation is exact).
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
      {selected && (
        <WorkVarianceDialog variance={selected}
                            onClose={() => setSelected(null)}
                            onChanged={() => { setSelected(null); load(); onReload?.(); }} />
      )}
    </Section>
  );
}


/* ---------- Certification tab ---------- */
function CertificationTab({ p, onReload }) {
  const [showCertify, setShowCertify] = useState(false);
  return (
    <Section title="Certification"
             hint="Five certification levels ladder a customer from data-capture proof up to LIVE invoicing."
             testId="shadow-cert-section">
      <div className="st-panel p-4 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-2 text-[11px]">
          {[
            ["L1", "Data capture"],
            ["L2", "Rate & commercial"],
            ["L3", "Statement match"],
            ["L4", "Invoice draft match"],
            ["L5", "LIVE billing certified"],
          ].map(([lvl, desc]) => (
            <div key={lvl}
                 className={`st-panel p-2 text-center ${p.certification_level >= parseInt(lvl.slice(1)) ? "bg-emerald-50" : ""}`}
                 data-testid={`shadow-cert-level-${lvl}`}>
              <div className="text-[10px] uppercase tracking-wider text-slate-500">{lvl}</div>
              <div className="text-[11px] font-semibold">{desc}</div>
              {p.certification_level >= parseInt(lvl.slice(1)) && (
                <CheckCircle2 className="h-4 w-4 text-emerald-600 mx-auto mt-1" />
              )}
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => setShowCertify(true)}
                  disabled={!["READY_FOR_CERTIFICATION", "VARIANCES_IDENTIFIED", "RECONCILING"].includes(p.status)}
                  data-testid="shadow-certify-btn">
            <ShieldCheck className="h-3.5 w-3.5 mr-1.5" /> Submit for certification
          </Button>
          {p.certification_number && (
            <div className="text-[11px] font-mono text-emerald-800">
              ✓ {p.certification_number} (L{p.certification_level})
            </div>
          )}
        </div>
      </div>
      {showCertify && (
        <CertifyDialog period={p}
                       onClose={() => setShowCertify(false)}
                       onCertified={() => { setShowCertify(false); onReload?.(); }} />
      )}
    </Section>
  );
}


/* ---------- History tab ---------- */
function HistoryTab({ periods, certifications }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Section title="All shadow periods" testId="shadow-history-periods">
        <div className="st-panel overflow-x-auto max-h-[70vh]">
          <table className="w-full text-[12px]">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 sticky top-0">
              <tr>
                <th className="text-left px-3 py-2">Shadow #</th>
                <th className="text-left px-3 py-2">Customer</th>
                <th className="text-left px-3 py-2">Period</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-right px-3 py-2">Variance</th>
              </tr>
            </thead>
            <tbody>
              {periods.map((p) => (
                <tr key={p.id} className="border-t border-slate-100">
                  <td className="px-3 py-1.5 font-mono text-[11px]">{p.shadow_number}</td>
                  <td className="px-3 py-1.5">{p.customer_name}</td>
                  <td className="px-3 py-1.5">{p.billing_period}</td>
                  <td className="px-3 py-1.5 text-[11px]">{p.status}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{fmt(p.variance_total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>
      <Section title="Certifications" testId="shadow-history-certs">
        <div className="st-panel overflow-x-auto max-h-[70vh]">
          <table className="w-full text-[12px]">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 sticky top-0">
              <tr>
                <th className="text-left px-3 py-2">Cert #</th>
                <th className="text-left px-3 py-2">Customer</th>
                <th className="text-left px-3 py-2">Period</th>
                <th className="text-left px-3 py-2">Level</th>
                <th className="text-left px-3 py-2">Date</th>
              </tr>
            </thead>
            <tbody>
              {certifications.map((c) => (
                <tr key={c.id} className="border-t border-slate-100">
                  <td className="px-3 py-1.5 font-mono text-[11px]">{c.certification_number}</td>
                  <td className="px-3 py-1.5">{c.customer_name}</td>
                  <td className="px-3 py-1.5">{c.billing_period}</td>
                  <td className="px-3 py-1.5">L{c.level}{c.with_conditions ? " (conditions)" : ""}</td>
                  <td className="px-3 py-1.5 text-[11px] text-slate-500">{c.certification_date?.slice(0, 10)}</td>
                </tr>
              ))}
              {certifications.length === 0 && (
                <tr><td colSpan={5} className="text-center py-8 text-[12px] text-slate-400">
                  No certifications yet.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Section>
    </div>
  );
}


/* -------------------------------------------------------------------------- */
/*  Dialogs                                                                   */
/* -------------------------------------------------------------------------- */
function CreatePeriodDialog({ onClose, onCreated }) {
  const [companies, setCompanies] = useState([]);
  const now = new Date();
  const [mc, setMc] = useState("");
  const [period, setPeriod] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`);
  const [legal, setLegal] = useState("");
  const [currency, setCurrency] = useState("INR");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/master-companies").then((r) => {
      setCompanies(r.data.items || r.data || []);
      if (r.data.items?.[0]) setMc(r.data.items[0].id);
    }).catch(() => {});
  }, []);

  const submit = async () => {
    if (!mc) { toast.error("Select a customer"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/shadow-billing/periods", {
        billing_period: period, master_company_id: mc,
        legal_entity: legal || undefined, currency,
      });
      toast.success(`Shadow ${data.period.shadow_number} created`);
      onCreated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="shadow-create-dialog">
        <DialogHeader><DialogTitle>New shadow billing period</DialogTitle></DialogHeader>
        <div className="space-y-3 text-[13px]">
          <label>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Customer</div>
            <select value={mc} onChange={(e) => setMc(e.target.value)}
                    className="w-full border rounded h-9 px-2"
                    data-testid="shadow-create-customer">
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
          <label>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Billing period (YYYY-MM)</div>
            <Input value={period} onChange={(e) => setPeriod(e.target.value)}
                   data-testid="shadow-create-period" />
          </label>
          <label>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Legal entity</div>
            <Input value={legal} onChange={(e) => setLegal(e.target.value)}
                   placeholder="Optional — e.g. IN, US-East"
                   data-testid="shadow-create-legal" />
          </label>
          <label>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Currency</div>
            <Input value={currency} onChange={(e) => setCurrency(e.target.value.toUpperCase())}
                   data-testid="shadow-create-currency" />
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="shadow-create-submit">
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function ChangeModeDialog({ profile, onClose, onChanged }) {
  const [mode, setMode] = useState(profile.billing_mode || "CONFIGURATION");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (reason.trim().length < 3) { toast.error("Reason is required"); return; }
    setBusy(true);
    try {
      await api.post(`/shadow-billing/profiles/${profile.master_company_id}/mode`,
                     { to_mode: mode, reason: reason.trim() });
      toast.success(`Mode changed to ${mode.replace(/_/g, " ")}`);
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="shadow-mode-dialog">
        <DialogHeader><DialogTitle>Change billing mode — {profile.customer_name}</DialogTitle></DialogHeader>
        <div className="space-y-3 text-[13px]">
          <label>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Target mode</div>
            <select value={mode} onChange={(e) => setMode(e.target.value)}
                    className="w-full border rounded h-9 px-2"
                    data-testid="shadow-mode-select">
              {BILLING_MODES.map((m) => <option key={m} value={m}>{m.replace(/_/g, " ")}</option>)}
            </select>
          </label>
          <label>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Reason (audit)</div>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)}
                      className="w-full border rounded p-2 text-[13px]" rows={3}
                      data-testid="shadow-mode-reason" />
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="shadow-mode-submit">
            Change
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function OverrideDialog({ profile, onClose, onChanged }) {
  const [active, setActive] = useState(!profile.emergency_override_active);
  const [reason, setReason] = useState("");
  const [finance, setFinance] = useState("");
  const [commercial, setCommercial] = useState("");
  const [hours, setHours] = useState(24);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (active && reason.trim().length < 6) { toast.error("Reason is required (6+ chars)"); return; }
    setBusy(true);
    try {
      await api.post(`/shadow-billing/profiles/${profile.master_company_id}/emergency-override`, {
        active, reason: reason.trim() || "revoke", hours_valid: hours,
        finance_approver: finance || "-", commercial_approver: commercial || "-",
      });
      toast.success(active ? "Emergency override activated" : "Override revoked");
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="shadow-override-dialog">
        <DialogHeader><DialogTitle>Live-invoice emergency override</DialogTitle></DialogHeader>
        <div className="space-y-3 text-[13px]">
          <div className="text-[11px] text-rose-800 bg-rose-50 p-2 rounded">
            <AlertTriangle className="h-3 w-3 inline mr-1" />
            Overrides skip the Shadow certification gate. Dual approval and a written reason are mandatory.
          </div>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
            <span>Activate override</span>
          </label>
          {active && (
            <>
              <label>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Reason</div>
                <textarea value={reason} onChange={(e) => setReason(e.target.value)}
                          className="w-full border rounded p-2" rows={3}
                          data-testid="shadow-override-reason" />
              </label>
              <div className="grid grid-cols-2 gap-2">
                <label>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Finance approver</div>
                  <Input value={finance} onChange={(e) => setFinance(e.target.value)}
                         data-testid="shadow-override-finance" />
                </label>
                <label>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Commercial approver</div>
                  <Input value={commercial} onChange={(e) => setCommercial(e.target.value)}
                         data-testid="shadow-override-commercial" />
                </label>
              </div>
              <label>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Valid for (hours)</div>
                <Input type="number" value={hours} onChange={(e) => setHours(Number(e.target.value))}
                       data-testid="shadow-override-hours" />
              </label>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="shadow-override-submit"
                  className={active ? "bg-rose-600 hover:bg-rose-700" : ""}>
            {active ? "Activate" : "Revoke"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function WorkVarianceDialog({ variance, onClose, onChanged }) {
  const [status, setStatus] = useState(variance.status);
  const [owner, setOwner] = useState(variance.owner_email || "");
  const [resolution, setResolution] = useState(variance.resolution || "");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/shadow-billing/variances/${variance.id}/update`, {
        status, owner_email: owner || undefined,
        resolution: resolution || undefined,
      });
      toast.success("Variance updated");
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="shadow-work-variance-dialog">
        <DialogHeader><DialogTitle>Work variance — {variance.order_id}</DialogTitle></DialogHeader>
        <div className="space-y-3 text-[13px]">
          <div className="st-panel p-2 text-[11px]">
            <div><span className="text-slate-500">Cause:</span> {variance.cause.replace(/_/g, " ")}</div>
            <div><span className="text-slate-500">Owner role:</span> {variance.owner_role}</div>
            <div><span className="text-slate-500">Reason:</span> {variance.reason}</div>
            <div><span className="text-slate-500">Variance:</span> {fmt(variance.variance_amount)}</div>
          </div>
          <label>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Status</div>
            <select value={status} onChange={(e) => setStatus(e.target.value)}
                    className="w-full border rounded h-9 px-2"
                    data-testid="shadow-work-status">
              {VARIANCE_STATES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
            </select>
          </label>
          <label>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Assign owner (email)</div>
            <Input value={owner} onChange={(e) => setOwner(e.target.value)}
                   data-testid="shadow-work-owner" />
          </label>
          <label>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Resolution note</div>
            <textarea value={resolution} onChange={(e) => setResolution(e.target.value)}
                      className="w-full border rounded p-2" rows={3}
                      data-testid="shadow-work-resolution" />
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="shadow-work-submit">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function CertifyDialog({ period, onClose, onCertified }) {
  const [level, setLevel] = useState(3);
  const [finance, setFinance] = useState("");
  const [commercial, setCommercial] = useState("");
  const [ops, setOps] = useState("");
  const [tech, setTech] = useState("");
  const [withConditions, setWithConditions] = useState(false);
  const [conditions, setConditions] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!finance || !commercial || !ops) { toast.error("Finance, Commercial, Operations approvers all required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/shadow-billing/periods/${period.id}/certify`, {
        level, finance_approver: finance, commercial_approver: commercial,
        operations_approver: ops, technical_approver: tech || undefined,
        with_conditions: withConditions, conditions: conditions || undefined,
      });
      toast.success(`Certified · ${data.certification.certification_number}`);
      onCertified?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="shadow-certify-dialog">
        <DialogHeader><DialogTitle>Certify · {period.shadow_number}</DialogTitle></DialogHeader>
        <div className="space-y-3 text-[13px]">
          <label>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Level</div>
            <select value={level} onChange={(e) => setLevel(Number(e.target.value))}
                    className="w-full border rounded h-9 px-2"
                    data-testid="shadow-certify-level">
              <option value={1}>L1 — Data capture certified</option>
              <option value={2}>L2 — Rate & commercial certified</option>
              <option value={3}>L3 — Billing statement certified</option>
              <option value={4}>L4 — Invoice draft certified (unlocks system draft)</option>
              <option value={5}>L5 — LIVE billing certified (unlocks production invoices)</option>
            </select>
          </label>
          <div className="grid grid-cols-2 gap-2">
            <label>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Finance approver</div>
              <Input value={finance} onChange={(e) => setFinance(e.target.value)}
                     data-testid="shadow-certify-finance" />
            </label>
            <label>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Commercial approver</div>
              <Input value={commercial} onChange={(e) => setCommercial(e.target.value)}
                     data-testid="shadow-certify-commercial" />
            </label>
            <label>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Operations approver</div>
              <Input value={ops} onChange={(e) => setOps(e.target.value)}
                     data-testid="shadow-certify-operations" />
            </label>
            <label>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Technical (optional)</div>
              <Input value={tech} onChange={(e) => setTech(e.target.value)}
                     data-testid="shadow-certify-technical" />
            </label>
          </div>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={withConditions}
                   onChange={(e) => setWithConditions(e.target.checked)} />
            <span>Certify with conditions</span>
          </label>
          {withConditions && (
            <textarea value={conditions} onChange={(e) => setConditions(e.target.value)}
                      placeholder="Describe residual risk that the approvers accept."
                      className="w-full border rounded p-2 text-[13px]" rows={3}
                      data-testid="shadow-certify-conditions" />
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="shadow-certify-submit">
            Certify
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



/* -------------------------------------------------------------------------- */
/*  Bulk Certify dialog (§5-§7 preview + confirm)                             */
/* -------------------------------------------------------------------------- */
function BulkCertifyDialog({ periodId, currency, onClose, onCertified }) {
  const [preview, setPreview] = useState(null);
  const [approver, setApprover] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [showExcluded, setShowExcluded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.post(
          `/shadow-billing/periods/${periodId}/bulk-certify-preview`, {});
        setPreview(data);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Preview failed");
        onClose();
      }
    })();
  }, [periodId, onClose]);

  const confirm = async () => {
    if (!approver.trim()) { toast.error("Finance approver name is required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(
        `/shadow-billing/periods/${periodId}/bulk-certify`,
        { approver_finance: approver.trim(), note: note.trim() || undefined });
      toast.success(`Bulk certified · Batch ${data.batch.batch_number} · ${data.certified_count} line(s)`);
      onCertified?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk certification failed");
    } finally { setBusy(false); }
  };

  if (!preview) {
    return (
      <Dialog open onOpenChange={onClose}>
        <DialogContent className="max-w-md" data-testid="shadow-bulk-preview-loading">
          <div className="py-10 text-center text-[12px] text-slate-400">Revalidating eligibility…</div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="shadow-bulk-certify-dialog">
        <DialogHeader><DialogTitle>Bulk certify exact matches</DialogTitle></DialogHeader>
        <div className="space-y-3 text-[13px]">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <FieldTile label="Period" value={preview.billing_period} />
            <FieldTile label="Customer" value={preview.customer_name || preview.master_company_id} />
            <FieldTile label="Legal entity" value={preview.legal_entity || "—"} />
            <FieldTile label="Currency" value={preview.currency} />
            <FieldTile label="Eligible" value={preview.eligible_count} bold tone="text-emerald-700" />
            <FieldTile label="Excluded" value={preview.excluded_count}
                        tone={preview.excluded_count ? "text-amber-800" : ""} />
            <FieldTile label="Subtotal" value={fmt(preview.subtotal, currency)} />
            <FieldTile label="Grand total" value={fmt(preview.grand_total, currency)} bold />
            <FieldTile label="Orders" value={preview.order_id_count} />
            <FieldTile label="Services" value={preview.service_count} />
          </div>
          {preview.excluded_count > 0 && (
            <div className="st-panel p-3 text-[12px]" data-testid="shadow-bulk-excluded">
              <button type="button" onClick={() => setShowExcluded((v) => !v)}
                      className="w-full text-left flex items-center justify-between">
                <span>
                  <AlertTriangle className="h-3 w-3 inline mr-1 text-amber-700" />
                  {preview.excluded_count} line(s) excluded by revalidation
                </span>
                <span className="text-[11px] text-slate-500">{showExcluded ? "hide" : "review"}</span>
              </button>
              {showExcluded && (
                <div className="mt-2 space-y-1 text-[11px]">
                  <div className="grid grid-cols-2 gap-1 mb-2">
                    {Object.entries(preview.exclusion_summary || {}).map(([k, n]) => (
                      <div key={k} className="flex justify-between">
                        <span className="text-slate-600">{k.replace(/_/g, " ")}</span>
                        <span className="font-mono">{n}</span>
                      </div>
                    ))}
                  </div>
                  <div className="max-h-40 overflow-y-auto border-t border-slate-100 pt-2">
                    {(preview.excluded_lines || []).slice(0, 50).map((e) => (
                      <div key={e.id} className="flex items-center gap-2 py-0.5">
                        <span className="font-mono text-slate-500">{e.order_id || "—"}</span>
                        <span className="text-slate-500">{e.service_code || "—"}</span>
                        <span className="ml-auto text-amber-800">{e.reason}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <label>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Finance approver (audit)</div>
            <Input value={approver} onChange={(e) => setApprover(e.target.value)}
                   placeholder="e.g. Priya Rao — Head of Finance"
                   data-testid="shadow-bulk-approver" />
          </label>
          <label>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Note (optional)</div>
            <Input value={note} onChange={(e) => setNote(e.target.value)}
                   data-testid="shadow-bulk-note" />
          </label>
          <div className="text-[11px] text-slate-500 flex items-start gap-2">
            <ShieldCheck className="h-3 w-3 mt-0.5 shrink-0 text-emerald-700" />
            Every eligible line was revalidated server-side against the latest reconciliation snapshot.
            Non-exact matches, possible duplicates, open variances and previously-certified lines are excluded automatically.
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={confirm} disabled={busy || preview.eligible_count === 0}
                  data-testid="shadow-bulk-confirm-btn">
            <ShieldCheck className="h-3.5 w-3.5 mr-1.5" />
            Certify {preview.eligible_count} line(s)
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
